import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../shared/config/apiBase';
import {
    WELLAND_USER_KEY,
    setWellandToken,
    clearWellandToken,
    markProfileJustLoadedSkipDashboardMe,
    clearDashboardMeSkipFlag,
} from '../shared/auth/storageKeys';
import { fetchCurrentUser } from '../shared/auth/fetchCurrentUser';
import { refreshAccessToken } from '../shared/auth/refreshAccessToken';
import { WELLAND_ACCESS_TOKEN_EVENT } from '../shared/auth/accessTokenEvents';

export const AuthContext = createContext({
    user: null,
    token: null,
    login: () => { },
    logout: () => { },
    refreshUser: async () => false,
    isAuthenticated: false,
    loading: true
});

function mergePictureFromPrevious(mergedUser) {
    try {
        const prevRaw = localStorage.getItem(WELLAND_USER_KEY);
        const prev = prevRaw ? JSON.parse(prevRaw) : null;
        if (
            prev &&
            mergedUser &&
            prev.email &&
            mergedUser.email &&
            prev.email === mergedUser.email &&
            !mergedUser.picture &&
            (prev.picture || prev.avatar || prev.photo)
        ) {
            return {
                ...mergedUser,
                picture: prev.picture || prev.avatar || prev.photo
            };
        }
    } catch {
    }
    return mergedUser;
}

export const AuthProvider = ({ children }) => {
    const LOGOUT_REDIRECT_FLAG = 'welland:logout-redirect-to-landing';
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    const clearLocalSession = useCallback(() => {
        setToken(null);
        setUser(null);
        clearWellandToken();
        localStorage.removeItem(WELLAND_USER_KEY);
        clearDashboardMeSkipFlag();
    }, []);

    useEffect(() => {
        const onAccessToken = (e) => {
            const t = e.detail?.token;
            if (t) setToken(t);
        };
        window.addEventListener(WELLAND_ACCESS_TOKEN_EVENT, onAccessToken);
        return () => window.removeEventListener(WELLAND_ACCESS_TOKEN_EVENT, onAccessToken);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // Access-токен хранится только в памяти — при загрузке страницы всегда
            // восстанавливаем сессию через httpOnly refresh-cookie (безопасно от XSS).
            const storedToken = await refreshAccessToken();
            if (!storedToken) {
                if (!cancelled) setLoading(false);
                return;
            }

            if (cancelled) return;
            setToken(storedToken);
            try {
                let u = await fetchCurrentUser(storedToken);
                if (cancelled) return;
                u = mergePictureFromPrevious(u);
                setUser(u);
                localStorage.setItem(WELLAND_USER_KEY, JSON.stringify(u));
                markProfileJustLoadedSkipDashboardMe();
            } catch {
                if (!cancelled) {
                    clearLocalSession();
                }
            }
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [clearLocalSession]);

    /**
     * @param {string} jwtToken
     * @param {{ user?: object, googlePicture?: string }} [options]
     */
    const login = useCallback(async (jwtToken, options = {}) => {
        if (!jwtToken) return;
        const { user: prefetchedUser, googlePicture } = options;

        setToken(jwtToken);
        setWellandToken(jwtToken);

        let nextUser = prefetchedUser;
        if (!nextUser) {
            try {
                nextUser = await fetchCurrentUser(jwtToken);
            } catch {
                clearLocalSession();
                return;
            }
        }

        nextUser = mergePictureFromPrevious(nextUser);
        if (googlePicture && !nextUser.picture) {
            nextUser = { ...nextUser, picture: googlePicture };
        }

        setUser(nextUser);
        localStorage.setItem(WELLAND_USER_KEY, JSON.stringify(nextUser));
        markProfileJustLoadedSkipDashboardMe();
    }, [clearLocalSession]);

    const logout = useCallback(async () => {
        const t = token;
        try {
            await fetch(apiUrl('/api/auth/logout'), {
                method: 'POST',
                credentials: 'include',
                headers: t ? { Authorization: `Bearer ${t}` } : {},
            });
        } catch {
            /* offline — всё равно чистим локально */
        }
        try {
            sessionStorage.setItem(LOGOUT_REDIRECT_FLAG, '1');
        } catch {
            /* noop */
        }
        clearLocalSession();
        if (typeof window !== 'undefined') {
            window.location.replace('/');
        }
    }, [token, clearLocalSession]);

    const refreshUser = useCallback(async () => {
        if (!token) return false;
        try {
            let u = await fetchCurrentUser(token);
            u = mergePictureFromPrevious(u);
            setUser(u);
            localStorage.setItem(WELLAND_USER_KEY, JSON.stringify(u));
            return true;
        } catch (e) {
            if (e.status === 401) {
                await logout();
            }
            return false;
        }
    }, [token, logout]);

    return (
        <AuthContext.Provider value={{ user, token, login, logout, refreshUser, isAuthenticated: !!token, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
